/**
 * String manipulation utilities
 */

import { pick } from "./object";

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function camelToSnake(str: string): string {
  return str.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`);
}

type RemoveUnderscoreFirstLetter<S extends string> = S extends `${infer FirstLetter}${infer U}`
  ? `${FirstLetter extends "_" ? U : `${FirstLetter}${U}`}`
  : S;

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? "_" : ""}${RemoveUnderscoreFirstLetter<Lowercase<T>>}${CamelToSnakeCase<U>}`
  : S;

type KeysToSnakeCase<T extends object> = {
  [K in keyof T as CamelToSnakeCase<K & string>]: T[K];
};

function toSnakeCase<T extends object>(obj: T) {
  return Object.keys(obj).reduce(
    (acc, key) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: required for object transformation
      ...acc,
      [camelToSnake(key)]: (obj as any)[key],
    }),
    {},
  ) as KeysToSnakeCase<T>;
}

export function pickSnake<T extends object, K extends keyof T>(obj: T, keys: K[]) {
  return toSnakeCase(pick(obj, keys));
}

export function t(key: string, ...args: string[]): string {
  return args
    .flatMap((arg) => arg.split(" "))
    .map((arg) => [key, arg].join(":"))
    .join(" ");
}

export function tryJsonParse<T>(json: string): T | null {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Formats a string value into segments based on the provided format pattern.
 *
 * @param value A string value to be formatted
 * @param format A string format that defines the segments. For example, "XX-XXXX-XXXXXXX-XX" will create segments of length 2, 4, 7, and 2.
 * @returns Value formatted into segments based on the provided format.
 * @example
 * ```
 * formatSegmented("123456789012345", "XX-XXXX-XXXXXXX-XX") // returns "12-3456-7890123-45"
 * ```
 */
export function formatSegmented(value: string, format: string): string {
  // Handle empty format case
  if (!format) {
    return "";
  }

  // Get segment lengths from the format
  const segmentLengths = format.split("-").map((segment) => segment.length);

  // Early return for single segment format (no hyphens)
  if (segmentLengths.length === 1) {
    return value.slice(0, segmentLengths[0]);
  }

  const segments: string[] = [];
  let remainingValue = value;

  // Process each segment
  for (const length of segmentLengths) {
    // Extract the segment from the remaining value
    const segment = remainingValue.slice(0, length);
    segments.push(segment);

    // Update the remaining value
    remainingValue = remainingValue.slice(length);
  }

  return segments.join("-");
}

export function toSentence(items: string[], separator = ", ", lastSeparator = " and "): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return items.join(lastSeparator);
  const lastItem = items.pop();
  return `${items.join(separator)}${lastSeparator}${lastItem}`;
}

export function toOrdinal(number: number): string {
  if (number === 1) return "1st";
  if (number === 2) return "2nd";
  if (number === 3) return "3rd";
  return `${number}th`;
}

/** Practical address shape: local@domain.tld. Stricter than HTML `type="email"` (`a@b`). */
export const EMAIL_ADDRESS_PATTERN = "[^\\s@]+@[^\\s@]+\\.[^\\s@]+";
const EMAIL_ADDRESS_REGEX = new RegExp(`^${EMAIL_ADDRESS_PATTERN}$`);

export function isValidEmailAddress(email: string): boolean {
  return EMAIL_ADDRESS_REGEX.test(email.trim());
}

/**
 * Undo common plus-address mangling before Auth lookups.
 * `+` in the local part is valid (RFC 5233) but form encoding and some
 * clients turn it into a space or `%2B`, which then misses the user.
 */
export function normalizeAuthEmail(email: string): string {
  const trimmed = email.normalize("NFKC").trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0) return trimmed;

  const local = trimmed.slice(0, at).replace(/%2b/gi, "+").replace(/ /g, "+");
  return `${local}@${trimmed.slice(at + 1)}`;
}
